import React from 'react'
import forge from 'node-forge'
import streamSaver from 'streamsaver'
import { Button, Modal, Progress } from 'antd'
import { confirmBase, PasswordInput } from 'utils/ui'
import { blobToBase64, decryptBlobAndToBase64, downloadFile } from 'utils/importExport/utils/export'
import { getCachedFile } from 'utils/attachmentCache'
import { getRsKeyObjFromResource } from 'pages/records/components/RecordCardContent'
import ResourceService from 'api/Resource'
import { SERVICE_TITLE, SERVICE_NAME } from 'service'
import { FILE_ACCESS_LEVEL } from 'types/utils'
import classNames from 'classnames'
import styles from './style.module.scss'

export const EXPORT_VERSION = '1.1'

export const formatElapsed = (ms) => {
  const totalSec = Math.round(ms / 1000)
  if (totalSec < 60) return `${totalSec}초`
  return `${Math.floor(totalSec / 60)}분 ${totalSec % 60}초`
}

const showExportResultModal = (content, t, elapsedMs = null) => {
  const isDev = process.env.REACT_APP_TARGET !== 'production'
  const modalContent =
    isDev && elapsedMs != null ? (
      <div>
        <div>{content}</div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
          소요 시간: {formatElapsed(elapsedMs)} (개발버전에서만 표시)
        </div>
      </div>
    ) : (
      content
    )

  confirmBase({
    wrapClassName: classNames(styles.decryptModal),
    icon: 'black',
    title: <div className={styles.decryptTitle}>{t('record.title.exportCompleted')}</div>,
    content: modalContent,
    okText: t('common.button.ok'),
    okButtonProps: { className: classNames('type7', 'w100', 'h38') },
    cancelButtonProps: { style: { display: 'none' } },
  })
}

const showFailedRecordsModal = (failedRecords, t) => {
  const getReasonText = (record) => {
    if (record.reason === 'files' && record.files?.length > 0) {
      return `${t('record.desc.exportFailedFilesTitle')}: ${record.files.join(', ')}`
    }
    if (record.reason === 'rsa_key') {
      return t('record.desc.exportFailedRsaKey')
    }
    if (record.reason === 'decrypt') {
      return t('record.desc.exportFailedDecrypt')
    }
    return t('record.desc.exportFailedUnknown')
  }

  confirmBase({
    icon: 'red',
    title: t('record.desc.exportSuccessWithFailures'),
    width: 480,
    content: (
      <div className={styles.failedModal}>
        <div className={styles.listTitle}>
          {t('record.desc.exportFailedRecordsList', { count: failedRecords.length })}
        </div>
        <div className={styles.recordList}>
          {failedRecords.map((record, idx) => (
            <div
              key={`failed-${record.title}-${record.reason}-${idx}`} // eslint-disable-line react/no-array-index-key
              className={styles.recordItem}
            >
              <div className={styles.recordTitle}>{record.title}</div>
              <div className={styles.recordReason}>{getReasonText(record)}</div>
            </div>
          ))}
        </div>
      </div>
    ),
    okText: t('common.button.ok'),
    cancelButtonProps: { style: { display: 'none' } },
  })
}

export const createSemaphore = (limit) => {
  let active = 0
  const queue = []
  return {
    acquire: () =>
      new Promise((resolve) => {
        if (active < limit) {
          active += 1
          resolve()
        } else {
          queue.push(resolve)
        }
      }),
    release: () => {
      active -= 1
      if (queue.length > 0) {
        active += 1
        queue.shift()()
      }
    },
  }
}

export const processRecord = async (record, fileSemaphore = null, allowFileDownload = true) => {
  const failedFiles = []
  let rsKeyObj = null

  try {
    rsKeyObj = await getRsKeyObjFromResource(record)
  } catch (err) {
    console.error(`Failed to get RSA key for record: ${record.title}`, err)
    throw new Error('rsa_key_error')
  }

  const items = await record.items.reduce(async (accPromise, item, idx) => {
    const acc = await accPromise
    const exportItem = {
      type: item.type,
      label: item.label,
      content: item.content,
      custom: item.custom || false,
      encrypt: item.encrypt || false,
      required: item.required || false,
      order: idx + 1,
    }

    if (item.encrypt === true && item.type !== 'file' && item.content != null) {
      try {
        exportItem.content = await rsKeyObj.decrypt(item.content)
      } catch (err) {
        console.error(`Failed to decrypt item: ${item.label} in record: ${record.title}`, err)
        exportItem.decryptFailed = true
      }
    }

    if (!allowFileDownload && item.type === 'files') {
      exportItem.content = []
    }

    if (
      allowFileDownload &&
      item.type === 'files' &&
      Array.isArray(item.content) &&
      item.content.length > 0
    ) {
      const convertedFiles = []

      const processFile = async (file) => {
        if (!file.url) {
          failedFiles.push(file.filename || 'unknown')
          return null
        }
        try {
          const cacheKey = file.url || `${file.filename}_${file.size}`
          let blob = await getCachedFile(cacheKey)
          const fromCache = !!blob

          if (!blob) {
            if (fileSemaphore) await fileSemaphore.acquire()
            try {
              let downloadUrl = file.download_url

              if (!downloadUrl) {
                const parts = file.url?.split('/file/')
                const objectKey = parts?.length > 1 ? parts[1] : null
                if (objectKey && record.id) {
                  const result = await ResourceService.getDownloadUrls(
                    [{ resource_id: record.id, object_key: objectKey }],
                    'download',
                    true,
                  )
                  downloadUrl = result.data?.files?.[0]?.download_url
                }
              }
              if (!downloadUrl) throw new Error('no_download_url')
              const response = await fetch(downloadUrl)
              if (!response.ok) throw new Error(`HTTP ${response.status}`)

              blob = await response.blob()
            } finally {
              if (fileSemaphore) fileSemaphore.release()
            }
          }

          let base64
          if (file.encrypt && !fromCache) {
            base64 = await decryptBlobAndToBase64(blob, rsKeyObj)
          } else {
            base64 = await blobToBase64(blob)
          }

          return {
            filename: file.filename,
            size: file.size,
            content_type: file.content_type,
            encrypt: file.encrypt || false,
            data: chunkBase64(base64),
          }
        } catch (err) {
          console.error(`Failed to download file: ${file.filename}`, err)
          failedFiles.push(file.filename || 'unknown')
          return null
        }
      }

      const results = await Promise.all(item.content.map(processFile))
      results.forEach((r) => r !== null && convertedFiles.push(r))

      exportItem.content = convertedFiles
    }

    acc.push(exportItem)
    return acc
  }, Promise.resolve([]))

  return {
    exportRecord: {
      template: {
        id: record.template?.id || record.template_id,
        name: record.template?.name || '',
        icon: record.template?.icon || record.template_icon || '',
        required_type: record.template?.required_type || '',
      },
      title: record.title,
      items,
    },
    failedFiles,
  }
}

export const makeProgressContent = (current, total, t) => (
  <div className={styles.progressModal}>
    <Progress
      percent={total > 0 ? Math.round((current / total) * 100) : 0}
      status={current < total ? 'active' : 'success'}
    />
    <div className={styles.progressCount}>
      {current} / {total}
    </div>
    <div className={styles.progressWarning}>{t('topBar.bulkExport.progressWarning')}</div>
  </div>
)

export const computeSHA256Hex = async (str) => {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str))
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// 단일 레코드의 최대 파일 용량 합산 → 동시 처리 메모리 압박 기준
export const getMaxRecordFileSize = (records) =>
  records.reduce((max, r) => {
    const recordSize = r.items
      .filter((i) => i.type === 'files' && Array.isArray(i.content))
      .reduce((s, i) => s + i.content.reduce((fs, f) => fs + (f.size || 0), 0), 0)
    return Math.max(max, recordSize)
  }, 0)

// 단일 레코드 파일 용량이 임계값 초과 시 순차(1), 아니면 병렬(3)
// 동시 메모리 ≈ threshold × RECORD_CONCURRENCY(3) × 1.33(base64) ≈ 20MB
export const HEAVY_SIZE_THRESHOLD = 20 * 1024 * 1024 // 20MB (10MB 파일 2개 수준, 동시 메모리 ≈ 80MB)

const processRecordsLoop = async (
  records,
  progressModal,
  t,
  onRecord,
  allowFileDownload = true,
) => {
  const FILE_CONCURRENCY = 3
  const maxRecordFileSize = allowFileDownload ? getMaxRecordFileSize(records) : 0
  const RECORD_CONCURRENCY = maxRecordFileSize > HEAVY_SIZE_THRESHOLD ? 1 : 3

  const fileSemaphore = createSemaphore(FILE_CONCURRENCY)
  const recordSemaphore = createSemaphore(RECORD_CONCURRENCY)
  const failedRecords = []

  // 처리 완료된 결과를 인덱스별로 보관 (write는 원본 순서대로)
  const resultBuffer = new Array(records.length).fill(undefined)
  let writeIndex = 0
  let completedCount = 0
  let flushChain = Promise.resolve()

  // 연속된 완료 결과를 순서대로 write (promise chain으로 직렬화)
  const flushBuffer = () => {
    flushChain = flushChain.then(async () => {
      while (writeIndex < records.length && resultBuffer[writeIndex] !== undefined) {
        const { record, exportRecord, failedFiles, error } = resultBuffer[writeIndex]
        const currentIndex = writeIndex
        writeIndex += 1

        if (error) {
          console.error(`Failed to process record: ${record.title}`, error)
          failedRecords.push({
            title: record.title,
            reason: error.message?.includes('rsa_key') ? 'rsa_key' : 'decrypt',
            error: error.message,
          })
        } else {
          // eslint-disable-next-line no-await-in-loop
          await onRecord(exportRecord, currentIndex)
          if (failedFiles.length > 0) {
            failedRecords.push({ title: record.title, reason: 'files', files: failedFiles })
          }
        }
      }
    })
  }

  // Worker Pool: 슬롯 빈 즉시 다음 레코드 처리 (처리 완료 시 슬롯 반환)
  await Promise.all(
    records.map(async (record, index) => {
      await recordSemaphore.acquire()
      try {
        const { exportRecord, failedFiles } = await processRecord(
          record,
          fileSemaphore,
          allowFileDownload,
        )
        resultBuffer[index] = { record, exportRecord, failedFiles, error: null }
      } catch (err) {
        resultBuffer[index] = { record, exportRecord: null, failedFiles: [], error: err }
      } finally {
        completedCount += 1
        progressModal?.update({ content: makeProgressContent(completedCount, records.length, t) })
        recordSemaphore.release()
        flushBuffer()
      }
    }),
  )

  await flushChain
  return failedRecords
}

const chunkBase64 = (str, size = 76) => str.match(new RegExp(`.{1,${size}}`, 'g')) ?? []

export const encryptForExport = async (plainText, password) => {
  const data = new TextEncoder().encode(plainText)
  const salt = crypto.getRandomValues(new Uint8Array(16))
  const iv = crypto.getRandomValues(new Uint8Array(16))

  const passwordRaw = new Uint8Array(password.length)
  for (let i = 0; i < password.length; i += 1) {
    passwordRaw[i] = password.charCodeAt(i) & 0xff // eslint-disable-line no-bitwise
  }

  const keyMaterial = await crypto.subtle.importKey('raw', passwordRaw, { name: 'PBKDF2' }, false, [
    'deriveKey',
  ])
  const key = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 100000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-CBC', length: 256 },
    false,
    ['encrypt'],
  )

  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, data)

  const combined = new Uint8Array(32 + ciphertext.byteLength)
  combined.set(salt, 0)
  combined.set(iv, 16)
  combined.set(new Uint8Array(ciphertext), 32)

  let binary = ''
  const CHUNK = 8192
  for (let i = 0; i < combined.length; i += CHUNK) {
    binary += String.fromCharCode(...combined.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}

// 파일 저장 경로 선택 (AbortError: null 반환으로 취소 표시, 미지원 브라우저: undefined 반환)
const pickSaveFileHandle = async (suggestedName, isEncrypted) => {
  if (typeof window.showSaveFilePicker !== 'function') return undefined
  try {
    return await window.showSaveFilePicker({
      suggestedName,
      types: [
        {
          description: 'JSON',
          accept: isEncrypted ? { 'text/plain': ['.enc'] } : { 'application/json': ['.json'] },
        },
      ],
    })
  } catch (e) {
    if (e.name === 'AbortError') return null
    return undefined
  }
}

const showFileAccessLimitModal = (t) =>
  new Promise((resolve) => {
    confirmBase({
      icon: 'red',
      title: t('topBar.bulkExport.fileAccessLimitTitle'),
      content: t('topBar.bulkExport.fileAccessLimitContent'),
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    })
  })

// 단일 레코드를 export 포맷(JSON + 체크섬)으로 조립
const buildExportData = async (exportRecord, password) => {
  const isEncrypted = !!password
  const recordJson = JSON.stringify(exportRecord)
  const md = forge.md.sha256.create()
  let records

  if (password) {
    const encBase64 = await encryptForExport(recordJson, password)
    md.update(encBase64)
    records = [chunkBase64(encBase64)]
  } else {
    md.update(recordJson)
    records = [exportRecord]
  }

  return {
    version: EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    ...(isEncrypted && { encrypted: true }),
    records,
    checksum: md.digest().toHex(),
  }
}

export const executeSingleExport = async ({
  individual,
  filename,
  password,
  t,
  messageApi,
  setExportLoading,
  allowFileDownload = true,
  onDestroyParentModals,
}) => {
  if (!allowFileDownload) {
    onDestroyParentModals?.()
    const confirmed = await showFileAccessLimitModal(t)
    if (!confirmed) return false
  }

  const isEncrypted = !!password
  const exportedFilename = `${filename}${isEncrypted ? '.json.enc' : '.json'}`
  const mimeType = isEncrypted ? 'text/plain' : 'application/json'

  const fileHandle = await pickSaveFileHandle(exportedFilename, isEncrypted)
  if (fileHandle === null) return false

  setExportLoading(true)
  messageApi.loading({
    content: t('record.desc.exportFileDownloading'),
    key: 'exportFileDownloading',
    duration: 0,
  })

  try {
    const { exportRecord, failedFiles } = await processRecord(individual, null, allowFileDownload)
    if (!exportRecord) return false

    const exportData = await buildExportData(exportRecord, password)
    const content = JSON.stringify(exportData, null, 2)

    if (fileHandle) {
      const writable = await fileHandle.createWritable()
      await writable.write(content)
      await writable.close()
    } else {
      downloadFile(content, exportedFilename, mimeType)
    }

    onDestroyParentModals?.()

    if (failedFiles.length > 0) {
      messageApi.warning(t('record.desc.exportPartialSuccess', { files: failedFiles.join(', ') }))
    } else {
      messageApi.open({
        type: 'success',
        content: isEncrypted
          ? t('record.result.exportEncryptedSuccess')
          : t('record.result.exportSuccess', { service: t(SERVICE_NAME) }),
      })
    }
    return true
  } catch (err) {
    console.error('Export error:', err)
    messageApi.error(t('record.error.exportFailed'))
    return false
  } finally {
    messageApi.destroy('exportFileDownloading')
    setExportLoading(false)
  }
}

const streamRecords = async (writable, records, progressModal, t, allowFileDownload = true) => {
  const enc = new TextEncoder()
  const md = forge.md.sha256.create()
  let isFirst = true

  await writable.write(
    enc.encode(`{
  "version": "${EXPORT_VERSION}",
  "exportedAt": "${new Date().toISOString()}",
  "records": [\n`),
  )

  const failedRecords = await processRecordsLoop(
    records,
    progressModal,
    t,
    async (exportRecord) => {
      const recordJson = JSON.stringify(exportRecord, null, 2).replace(/^/gm, '    ')
      await writable.write(enc.encode(`${isFirst ? '' : ',\n'}${recordJson}`))
      md.update(recordJson)
      isFirst = false
    },
    allowFileDownload,
  )

  const checksum = md.digest().toHex()
  await writable.write(enc.encode(`\n  ],\n  "checksum": "${checksum}"\n}`))
  await writable.close()
  return failedRecords
}

const streamEncryptedRecords = async (
  writable,
  records,
  password,
  progressModal,
  t,
  allowFileDownload = true,
) => {
  const enc = new TextEncoder()
  const md = forge.md.sha256.create()
  let isFirst = true

  await writable.write(
    enc.encode(`{
  "version": "${EXPORT_VERSION}",
  "exportedAt": "${new Date().toISOString()}",
  "encrypted": true,
  "records": [\n`),
  )

  const failedRecords = await processRecordsLoop(
    records,
    progressModal,
    t,
    async (exportRecord) => {
      const encBase64 = await encryptForExport(JSON.stringify(exportRecord), password)
      await writable.write(
        enc.encode(`${isFirst ? '' : ',\n'}    ${JSON.stringify(chunkBase64(encBase64))}`),
      )
      md.update(encBase64)
      isFirst = false
    },
    allowFileDownload,
  )

  const checksum = md.digest().toHex()
  await writable.write(enc.encode(`\n  ],\n  "checksum": "${checksum}"\n}`))
  await writable.close()

  return failedRecords
}

const executeBulkExport = async (
  scope,
  filename,
  password,
  t,
  messageApi,
  onDestroyParentModals,
) => {
  let progressModal = null

  const destroyProgress = () => {
    progressModal?.destroy()
    progressModal = null
  }

  const isEncrypted = !!password
  const exportedFilename = `${filename}${isEncrypted ? '.json.enc' : '.json'}`

  try {
    const LIMIT = 100
    const allRecords = []
    let offset = 0
    let hasMore = true
    let fileAccessLevel = null

    while (hasMore) {
      // eslint-disable-next-line no-await-in-loop
      const response = await ResourceService.bulkGet(scope, offset, LIMIT)
      const batch = response.data || []
      allRecords.push(...batch)

      if (offset === 0) fileAccessLevel = response.fileAccessLevel

      hasMore = batch.length === LIMIT
      offset += LIMIT
    }

    const allowFileDownload = fileAccessLevel === FILE_ACCESS_LEVEL.DOWNLOAD_AND_PREVIEW
    if (!allowFileDownload) {
      onDestroyParentModals?.()
      const confirmed = await showFileAccessLimitModal(t)
      if (!confirmed) return false
    }

    if (allRecords.length === 0) {
      onDestroyParentModals?.()
      messageApi.error(t('topBar.bulkExport.noRecords'))
      return false
    }

    const fileHandle = await pickSaveFileHandle(exportedFilename, isEncrypted)
    if (fileHandle === null) return false

    progressModal = Modal.confirm({
      title: t('topBar.bulkExport.loading'),
      icon: null,
      width: 480,
      content: makeProgressContent(0, allRecords.length, t),
      closable: false,
      keyboard: false,
      maskClosable: false,
      okButtonProps: { style: { display: 'none' } },
      cancelButtonProps: { style: { display: 'none' } },
      wrapClassName: classNames('om-popup-1', styles.progressModalWrapper),
    })

    const startTime = Date.now()

    const writable = fileHandle
      ? await fileHandle.createWritable()
      : streamSaver.createWriteStream(exportedFilename).getWriter()

    const failedRecords = isEncrypted
      ? await streamEncryptedRecords(
          writable,
          allRecords,
          password,
          progressModal,
          t,
          allowFileDownload,
        )
      : await streamRecords(writable, allRecords, progressModal, t, allowFileDownload)

    destroyProgress()
    onDestroyParentModals?.()

    if (failedRecords.length > 0) {
      showFailedRecordsModal(failedRecords, t)
    } else {
      showExportResultModal(
        isEncrypted
          ? t('record.result.exportEncryptedSuccessWithCount', { count: allRecords.length })
          : t('record.result.exportSuccessWithCount', { count: allRecords.length }),
        t,
        Date.now() - startTime,
      )
    }
    return true
  } catch (err) {
    console.error('Bulk export error:', err)
    destroyProgress()
    onDestroyParentModals?.()
    messageApi.error(t('topBar.bulkExport.apiFailed'))
    return false
  }
}

export const showSkipWarningModal = ({ t, styles: modalStyles, encryptModalRef, onExport }) => {
  const warningModalRef = { current: null }

  const renderContent = (isExporting) => (
    <div>
      <div className={modalStyles.warningModal}>
        <div className={modalStyles.question}>{t('record.desc.exportSkipWarningQuestion')}</div>
        <div className={modalStyles.warningText}>{t('record.desc.exportSkipWarningDesc')}</div>
      </div>
      <div className={modalStyles.encryptModalFooter}>
        <div />
        <div className={modalStyles.rightButtons}>
          <Button disabled={isExporting} onClick={() => warningModalRef.current.destroy()}>
            {t('record.button.no')}
          </Button>
          <Button
            type="primary"
            loading={isExporting}
            onClick={isExporting ? undefined : handleExport}
          >
            {t('record.button.export')}
          </Button>
        </div>
      </div>
    </div>
  )

  const setExporting = (isExporting) => {
    warningModalRef.current?.update({
      keyboard: !isExporting,
      maskClosable: !isExporting,
      content: renderContent(isExporting),
    })
  }

  const handleExport = async () => {
    setExporting(true)
    await onExport(warningModalRef, encryptModalRef)
    setExporting(false)
  }

  warningModalRef.current = confirmBase({
    icon: 'red',
    title: t('record.title.exportPasswordsToFile'),
    width: 520,
    wrapClassName: modalStyles.customFooterModal,
    content: renderContent(false),
    okButtonProps: { style: { display: 'none' } },
    cancelButtonProps: { style: { display: 'none' } },
  })
}

export const showEncryptModal = ({
  t,
  styles: modalStyles,
  messageApi,
  onEncryptExport,
  onPlainExport,
}) => {
  const passwordRef = { value: '', confirm: '' }
  const modalRef = { current: null }

  const renderContent = (isExporting) => (
    <div className={modalStyles.encryptModalContent}>
      <div className={modalStyles.encryptModal}>
        <div className={modalStyles.description}>{t('record.desc.exportSetPassword')}</div>
        <div className={modalStyles.passwordField}>
          <PasswordInput
            disabled={isExporting}
            onChange={(e) => {
              passwordRef.value = e.target.value
            }}
            onPressEnter={isExporting ? undefined : handleEncryptExport}
            placeholder={t('record.field.enterPassword')}
            maxLength={50}
          />
        </div>
        <div className={modalStyles.passwordField}>
          <PasswordInput
            disabled={isExporting}
            onChange={(e) => {
              passwordRef.confirm = e.target.value
            }}
            onPressEnter={isExporting ? undefined : handleEncryptExport}
            placeholder={t('record.field.confirmPassword')}
            maxLength={50}
          />
        </div>
      </div>
      <div className={modalStyles.encryptModalFooter}>
        <Button
          disabled={isExporting}
          onClick={() =>
            showSkipWarningModal({
              t,
              styles: modalStyles,
              encryptModalRef: modalRef,
              onExport: onPlainExport,
            })
          }
        >
          {t('record.button.skip')}
        </Button>
        <div className={modalStyles.rightButtons}>
          <Button
            disabled={isExporting}
            onClick={() => modalRef.current.destroy()}
            className={classNames('h38')}
          >
            {t('record.button.no')}
          </Button>
          <Button
            type="primary"
            loading={isExporting}
            onClick={isExporting ? undefined : handleEncryptExport}
            className={classNames('type7', 'h38')}
          >
            {t('record.button.export')}
          </Button>
        </div>
      </div>
    </div>
  )

  const setExporting = (isExporting) => {
    modalRef.current?.update({
      keyboard: !isExporting,
      maskClosable: !isExporting,
      content: renderContent(isExporting),
    })
  }

  const handleEncryptExport = async () => {
    const validatePassword = (value) => {
      if (!value) return t('record.desc.exportEncryptDesc')
      if (value.length < 4) return t('validation.minLength.invalid', { length: 4 })
      if (/[^\x21-\x7e]/.test(value)) return t('validation.asciiPrintable.invalid')
      return null
    }

    const valueError = validatePassword(passwordRef.value)
    if (valueError) {
      messageApi.error(valueError)
      return
    }

    const confirmError = validatePassword(passwordRef.confirm)
    if (confirmError) {
      messageApi.error(confirmError)
      return
    }

    if (passwordRef.value !== passwordRef.confirm) {
      messageApi.error(t('record.desc.passwordMismatch'))
      return
    }

    setExporting(true)
    const password = passwordRef.value
    const completed = await onEncryptExport(password, modalRef)
    if (completed !== false) {
      passwordRef.value = ''
      passwordRef.confirm = ''
    } else {
      setExporting(false)
    }
  }

  modalRef.current = confirmBase({
    icon: 'red',
    title: t('record.title.exportPasswordsToFile'),
    width: 520,
    wrapClassName: modalStyles.customFooterModal,
    content: renderContent(false),
    okButtonProps: { style: { display: 'none' } },
    cancelButtonProps: { style: { display: 'none' } },
  })
}

export const getFileTimestamp = () => {
  const now = new Date()
  const yy = String(now.getFullYear()).slice(-2)
  const MM = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const HH = String(now.getHours()).padStart(2, '0')
  const mm = String(now.getMinutes()).padStart(2, '0')
  return `${yy}${MM}${dd}${HH}${mm}`
}

export const showFilenameConfirmModal = ({
  t,
  styles: modalStyles,
  defaultFilename,
  onConfirm,
}) => {
  confirmBase({
    icon: 'red',
    title: t('record.desc.exportConfirmTitle'),
    width: 480,
    content: (
      <div className={modalStyles.exportModal}>
        <div className={modalStyles.title}>{t('record.desc.exportConfirmQuestion')}</div>
      </div>
    ),
    okText: t('common.button.yes'),
    cancelText: t('common.button.no'),
    okButtonProps: { className: classNames('type7', 'w100', 'h38') },
    onOk: () => onConfirm(defaultFilename),
  })
}

// 파일명 확인 → 암호화 여부 → 실행까지 이어지는 공통 export 플로우
// onExecute({ filename, password, onDestroyParentModals }): 실제 export 실행 함수
export const runExportFlow = ({
  defaultFilename,
  t,
  styles: modalStyles,
  messageApi,
  onExecute,
}) => {
  showFilenameConfirmModal({
    t,
    styles: modalStyles,
    defaultFilename,
    onConfirm: (filename) =>
      showEncryptModal({
        t,
        styles: modalStyles,
        messageApi,
        onEncryptExport: async (password, encryptModalRef) => {
          const completed = await onExecute({ filename, password })
          if (completed !== false) encryptModalRef.current?.destroy()
          return completed
        },
        onPlainExport: async (warningModalRef, encryptRef) => {
          const completed = await onExecute({
            filename,
            password: null,
            onDestroyParentModals: () => warningModalRef.current?.destroy(),
          })
          if (completed !== false) encryptRef.current?.destroy()
        },
      }),
  })
}

const handleBulkExport = (scope, t, messageApi) => {
  runExportFlow({
    defaultFilename: `${SERVICE_TITLE}-${getFileTimestamp()}`,
    t,
    styles,
    messageApi,
    onExecute: ({ filename, password, onDestroyParentModals }) =>
      executeBulkExport(scope, filename, password, t, messageApi, onDestroyParentModals),
  })
}

export default handleBulkExport
